import { ColumnsType } from 'antd/lib/table';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DeleteButton, EditButton, Space, Table } from '~/components';
import { useGetContactsApi, useMutateContactsApi } from '~/controllers/contacts';
import { useGetApplication } from '~/controllers/wizard';
import { CheckThinIcon } from '~/img';
import { FormItemInjectedProps } from '~/types/FormItemInjectedProps';
import { IndividualRowType } from '~/types/IndividualRowType';
import { Person } from '~/types/Person';
import { getAuthorizedRepresentativesData } from '~/utils/getAuthorizedRepresentativesData';
import { getTableRowsErros } from '~/utils/getTableRowsErros';
import { hasSoftDeclineFields } from '~/utils/softDeclinesFields';

import { isShareholderEditButtonDisabled } from '../ShareholdersMultiple/utils';
import AuthorizedRepresentativeAdd from './AuthorizedRepresentativeAdd';
import AuthorizedRepresentativeEdit from './AuthorizedRepresentativeEdit';

const AuthorizedRepresentativeTable: React.FC<Partial<FormItemInjectedProps<Person[]>>> = (props) => {
  const { value, onChange } = props as FormItemInjectedProps<Person[]>;
  const { t } = useTranslation('fillApplication');
  const applicationQuery = useGetApplication();
  const hasIndividualsValidationWarnings = hasSoftDeclineFields(applicationQuery.data, 'authorizedRepresentative');
  const globeState = useSelector((state: any) => state?.state);
  const { commonFormErrors } = globeState;
  const allContacts: any = useGetContactsApi();
  const contactMutation = useMutateContactsApi();
  const tableRowErrors = getTableRowsErros({
    commonFormErrors: commonFormErrors['/wizard/application/authorizedRepresentative'],
  });
  const { requiredFields, errorRowsKeys } = tableRowErrors;
  const [uid, setUid] = useState('');
  const [sourceOfEditing, setSourceOfEditing] = useState('');
  const openEdit = (uid: string) => {
    setUid(uid);
  };
  const onClose = () => {
    setUid('');
  };
  const authorizedRepresentativesDataSource = getAuthorizedRepresentativesData(
    value,
    onChange,
    openEdit,
    sourceOfEditing,
    contactMutation,
    allContacts?.data,
  );
  const individualColumns: ColumnsType<IndividualRowType> = useMemo(
    () => [
      {
        title: t('First name'),
        dataIndex: 'firstName',
        key: 'firstName',
      },
      {
        title: t('Last name'),
        dataIndex: 'lastName',
        key: 'lastName',
      },
      {
        title: t('Passport'),
        dataIndex: 'hasPassport',
        key: 'hasPassport',
        render: (hasPassport: boolean) => {
          if (hasPassport) return <CheckThinIcon />;
          return null;
        },
      },
      {
        title: t('Emirates ID'),
        dataIndex: 'hasEmiratesId',
        key: 'hasEmiratesId',
        render: (hasEmiratesId: boolean) => {
          if (hasEmiratesId) return <CheckThinIcon />;
          return null;
        },
      },
      {
        title: t('Authorized Signatory'),
        dataIndex: 'isAuthorizedSignatory',
        key: 'isAuthorizedSignatory',
        render: (isAuthorizedSignatory: boolean) => {
          if (isAuthorizedSignatory) return <CheckThinIcon />;
          return null;
        },
      },
      {
        title: '',
        key: 'action',
        fixed: 'right',
        render: (_, { onEdit, onDelete }, index) => (
          <Space size={16} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <EditButton
              id="individualTable_EditButton"
              disabled={isShareholderEditButtonDisabled(applicationQuery.data, hasIndividualsValidationWarnings)}
              hasValidationWarnings={hasIndividualsValidationWarnings}
              onClick={onEdit}
              hasError={errorRowsKeys?.includes(`${index}`)}
            />
            <DeleteButton
              id="individualTable_DeleteButton"
              disabled={isShareholderEditButtonDisabled(applicationQuery.data, hasIndividualsValidationWarnings)}
              onClick={onDelete}
            />
          </Space>
        ),
      },
    ],
    [hasIndividualsValidationWarnings, applicationQuery.data, t, errorRowsKeys],
  );

  return (
    <Space size={24} direction="vertical">
      <Table
        id="authorizedRepresentative"
        columns={individualColumns}
        dataSource={authorizedRepresentativesDataSource}
        loading={applicationQuery.isLoading}
        pagination={{ hideOnSinglePage: true }}
        rowKey="uid"
      />
      {authorizedRepresentativesDataSource.length < 10 && (
        <AuthorizedRepresentativeAdd
          setSourceOfEditing={setSourceOfEditing}
          {...props}
          value={authorizedRepresentativesDataSource as any}
        />
      )}
      <AuthorizedRepresentativeEdit
        uid={uid}
        onClose={onClose}
        setSourceOfEditing={setSourceOfEditing}
        authorizedRepresentativesDataSource={authorizedRepresentativesDataSource}
        {...props}
      />
    </Space>
  );
};

export default AuthorizedRepresentativeTable;
